import { createSlice } from "@reduxjs/toolkit";

import { NormalizeFeaturePackage, WarnDetails } from "components/ml/types";

const initialState: {
  errorMessage: string | null;
  errorType: string | null;
  warnDetails: WarnDetails | null;
  targetFeature: object | null;
  inputFeatures: object[];
  featureImportanceSummary: object | null;
  normalizationFeatures: NormalizeFeaturePackage[];
} = {
  errorMessage: null,
  errorType: null,
  warnDetails: null,
  targetFeature: null,
  inputFeatures: [],
  featureImportanceSummary: null,
  normalizationFeatures: []
};

const mlSlice = createSlice({
  name: "ml",
  initialState,
  reducers: {
    setError(state, action) {
      state.errorMessage = action.payload?.error;
      state.errorType = action.payload?.code;
      const details = action.payload?.details;
      if (details) {
        // backend only send camelCase but also support legacy snake_case here
        state.warnDetails = {
          featuresWithNaOrZero:
            details.featuresWithNaOrZero ?? details.features_with_na_or_zero ?? [],
          featuresThatCanBeZero:
            details.featuresThatCanBeZero ?? details.features_that_can_be_zero ?? [],
          droppedWells: details.droppedWells ?? details.dropped_wells ?? [],
          droppedWellCount: details.droppedWellCount ?? details.dropped_well_count ?? 0,
          featuresWithSingleValue:
            details.featuresWithSingleValue ?? details.features_with_single_value ?? {},
          normalizationSummary:
            details.normalizationSummary ?? details.normalization_summary ?? []
        };
      } else {
        state.warnDetails = null;
      }
    },
    clearError(state) {
      state.errorMessage = null;
      state.errorType = null;
      state.warnDetails = null;
    },
    setTargetFeature(state, action) {
      state.targetFeature = action.payload;
    },
    setInputFeatures(state, action) {
      state.inputFeatures = action.payload;
    },
    setSummary(state, action) {
      state.featureImportanceSummary = action.payload;
    },
    clearSummary(state) {
      state.featureImportanceSummary = null;
    },
    setMLNormalizationFeatureSetting(state, action) {
      const { applyTo, normalizeBy } = action.payload ?? {};
      if (!applyTo?.Property) return;

      const existing = Array.isArray(state.normalizationFeatures)
        ? state.normalizationFeatures
        : [];

      const withoutApplyTo = existing.filter(
        (item: NormalizeFeaturePackage) => item?.ApplyTo?.Property !== applyTo.Property
      );

      if (!normalizeBy) {
        state.normalizationFeatures = withoutApplyTo;
        return;
      }

      state.normalizationFeatures = [
        ...withoutApplyTo,
        {
          ApplyTo: applyTo,
          NormalizeBy: normalizeBy
        }
      ];
    },
    /**
     * Bulk-replaces the entire normalizationFeatures array.
     * Used when loading a template or resetting to defaults.
     */
    setMLNormalizationFeatures(state, action) {
      state.normalizationFeatures = Array.isArray(action.payload) ? action.payload : [];
    },
    resetMLSlice: () => initialState
  }
});

export const {
  setError,
  clearError,
  resetMLSlice,
  setTargetFeature,
  setInputFeatures,
  setSummary,
  clearSummary,
  setMLNormalizationFeatureSetting,
  setMLNormalizationFeatures
} = mlSlice.actions;

export default mlSlice.reducer;
